import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  Container,
  makeStyles,
  useTheme,
  Button,
  Collapse,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import axios from "axios";
import AxiosErrorHandling from "../../../components/AxiosErrorHandling/AxiosErrorHandling";
import DashboardSkeleton from "../../../UI/Skeletons/DashboardSkeleton";
import moment from "moment";
import PeriodSelection from "../../../components/PeriodSelection/PeriodSelection";
import DateFnsUtils from "@date-io/date-fns";
import Scorecard from "../../../components/Scorecard/Scorecard";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import SendFill from "../../../Assets/CustomIcons/SendFill";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import LastModified from "../../../components/LastModified/LastModified";
import ConfigurationListsContext from "../../../context/configurationListsContext";
import SignalManagerCompanyPotentialCandidates from "./SignalManagerCompanyPotentialCandidates";

const DRAWER_WIDTH = 240;
const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "120px",
    "& .MuiPaper-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDivider-root": {
      height: 1,
      width: "100%",
      marginTop: theme.spacing(1),
    },
  },
  companyItemRoot: {
    "& .MuiChip-root": {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  topSearchBar: {
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    position: "fixed",
    top: 0,
    zIndex: 10,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    width: "calc(100% - " + DRAWER_WIDTH + "px)",
  },
  topHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dot: {
    width: 8,
    height: 8,
    display: "flex",
    alignItems: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  actionButtonHandle: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
  buttonProgress: {
    color: "#fff",
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SignalManager = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const [error, setError] = useState(null);
  const [periodTag, setPeriodTag] = useState("yesterday");
  const [periodDates, setPeriodDates] = useState({
    from: moment().subtract(1, "days").format("YYYY/MM/DD"),
    to: moment().subtract(1, "days").format("YYYY/MM/DD"),
  });
  const [customDateFrom, setCustomDateFrom] = React.useState(
    moment().format("YYYY/MM/DD")
  );
  const [customDateTo, setCustomDateTo] = React.useState(
    moment().format("YYYY/MM/DD")
  );
  const [generalData, setGeneralData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false); //if true, holds the company id
  const [companyPotentialDetails, setCompanyPotentialDetails] = useState(null);
  useEffect(() => {
    const GetGeneralData = async () => {
      try {
        setIsLoading(true);
        const data = await axios.post(
          "/wp-json/api/v2/signal_manager_dashboard",
          { period: periodDates }
        );
        setIsLoading(false);
        setGeneralData(data.data);
      } catch (err) {
        setError(err.response);
        setIsLoading(false);
      }
    };
    GetGeneralData();
  }, [periodTag, periodDates]);

  const handleCustomDateCollection = () => {
    setPeriodDates({
      from: customDateFrom,
      to: customDateTo,
    });
  };

  const handleCompanyHandled = async (itemId) => {
    const localCompanies = generalData.companies;
    const idIndex = localCompanies.findIndex((comp) => comp.id === itemId);
    if (idIndex > -1) {
      localCompanies[idIndex].is_new = 0;
      await axios.post(`/wp-json/api/v2/company_handled/${itemId}`);
    }
    setGeneralData((prev) => ({ ...prev, companies: localCompanies }));
  };

  const handleCancelDateCollection = () => {
    setPeriodTag("yesterday");
  };

  const handlePeriodChange = (event) => {
    setPeriodTag(event.target.value);
    let localPeriod = {
      from: moment().subtract(7, "days").format("YYYY/MM/DD"),
      to: moment().format("YYYY/MM/DD"),
    };
    switch (event.target.value) {
      case "today":
        localPeriod = {
          from: moment().format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };

        break;
      case "yesterday":
        localPeriod = {
          from: moment().subtract(1, "days").format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };

        break;
      case "last7":
        localPeriod = {
          from: moment().subtract(7, "days").format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };
        break;
      case "last28":
        localPeriod = {
          from: moment().subtract(28, "days").format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };
        break;
      case "last90":
        localPeriod = {
          from: moment().subtract(90, "days").format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };
        break;
      case "thisYear":
        localPeriod = {
          from: moment(`${moment().year()}/1/1`, "YYYY/MM/DD").format(
            "YYYY/MM/DD"
          ),
          to: moment(`${moment().year()}/12/31`, "YYYY/MM/DD").format(
            "YYYY/MM/DD"
          ),
        };
        break;

      default:
        localPeriod = {
          from: moment().subtract(7, "days").format("YYYY/MM/DD"),
          to: moment().format("YYYY/MM/DD"),
        };
        break;
    }
    setPeriodDates(localPeriod);
  };

  const get_unique_categories_sum = () => {
    let uniqueCategories = [];
    if (generalData.companies.length > 0) {
      generalData.companies.forEach((comp) => {
        comp.suggested_categories.forEach((sc) => {
          if (!uniqueCategories.includes(parseInt(sc)))
            uniqueCategories = [...uniqueCategories, parseInt(sc)];
        });
      });
    }
    return uniqueCategories.length;
  };

  const handleViewPotentialCandidates = async (companyId) => {
    //build the filter
    const { companies } = generalData;
    setButtonLoading(companyId);
    try {
      const localCompanyIndex = companies.findIndex(
        (comp) => comp.id === companyId
      );

      let filter = null;
      if (localCompanyIndex > -1) {
        setCompanyPotentialDetails({
          company: companies[localCompanyIndex],
        });
        filter = {
          filter_categories: companies[localCompanyIndex].suggested_categories,
          cities: companies[localCompanyIndex].cities.split(", "),
        };
      }
      const potential_candidates = await axios.post(
        "/wp-json/api/v2/potential_signal_candidates",
        {
          filter,
          distribution_id: parseInt(
            companies[localCompanyIndex].signal_distribution
          ),
          show_candidates_summary: true,
        }
      );
      setCompanyPotentialDetails({
        company: companies[localCompanyIndex],
        details: potential_candidates.data,
      });
      setButtonLoading(false);
    } catch (err) {
      setButtonLoading(false);
      setError(err.response);
    }
  };

  return (
    <React.Fragment>
      <Paper width={1} elevation={0} square className={classes.topSearchBar}>
        <div className={classes.topHeader}>
          <Grid
            container
            spacing={0}
            justify="flex-start"
            alignItems={periodTag === "custom" ? "flex-start" : "center"}
          >
            <Grid item xs={2} style={{ padding: 0 }}>
              <Box pb={periodTag === "custom" ? 2 : 0}>
                <PeriodSelection
                  selectedPeriod={periodTag}
                  onPeriodChange={handlePeriodChange}
                  filled
                  fullWidth
                  noMargin
                />
                <Collapse in={periodTag === "custom"}>
                  <React.Fragment>
                    <Grid container>
                      <Grid item xs={8}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            format="yyyy/MM/dd"
                            margin="normal"
                            id="date-picker-from"
                            label="From Date"
                            minDate={moment()
                              .subtract(365, "days")
                              .format("YYYY/MM/DD")}
                            maxDate={moment().format("YYYY/MM/DD")}
                            value={customDateFrom}
                            onChange={(date) => setCustomDateFrom(date)}
                            KeyboardButtonProps={{
                              "aria-label": "fromDate",
                            }}
                            style={{
                              marginTop: 8,
                              marginRight: theme.spacing(2),
                            }}
                          />
                          <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            format="yyyy/MM/dd"
                            margin="normal"
                            id="date-picker-from"
                            label="End Date"
                            minDate={moment(customDateFrom).format(
                              "YYYY/MM/DD"
                            )}
                            value={customDateTo}
                            onChange={(date) => setCustomDateTo(date)}
                            KeyboardButtonProps={{
                              "aria-label": "toDate",
                            }}
                            style={{
                              marginTop: 8,
                              marginRight: theme.spacing(2),
                            }}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleCustomDateCollection}
                        >
                          Apply
                        </Button>
                        <Button onClick={handleCancelDateCollection}>
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                </Collapse>
              </Box>
            </Grid>
            <Grid item xs={8}></Grid>
            <Grid
              item
              xs={2}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                endIcon={<SendFill />}
                onClick={() =>
                  props.history.push("/companies/signal-manager/preview")
                }
              >
                Send Signals
              </Button>
            </Grid>
          </Grid>
        </div>
      </Paper>
      <Container maxWidth={"xl"} className={classes.root}>
        {isLoading ? (
          <DashboardSkeleton open={true} />
        ) : (
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <AxiosErrorHandling
                  title={"Failed to view potential candidates"}
                  error={error}
                />
              </Grid>
            )}

            <Grid item xs={2}>
              <Scorecard
                title="Total Distributions"
                value={generalData && generalData.distributions}
                barColor="#FFCE54"
              />
            </Grid>
            <Grid item xs={2}>
              <Scorecard
                title="Total companies"
                value={generalData && generalData.companies.length}
                barColor="#48CFAD"
              />
            </Grid>
            <Grid item xs={2}>
              <Scorecard
                title="Total Candidates"
                value={generalData && generalData.total_candidates}
                barColor="#ED5565"
              />
            </Grid>
            <Grid item xs={2}>
              <Scorecard
                title="Total Categories"
                value={
                  generalData &&
                  generalData.companies &&
                  get_unique_categories_sum()
                }
                barColor="#5D9CEC"
              />
            </Grid>
            <Grid item xs={2}>
              <Scorecard
                title="Companies Responded"
                value={
                  generalData &&
                  generalData.companies.filter(
                    (comp) => comp.responded_categories.length > 0
                  ).length
                }
                barColor="#AC92EC"
              />
            </Grid>
            <Grid item xs={2}>
              <Scorecard
                title="New Responded"
                value={
                  generalData &&
                  generalData.companies.filter(
                    (comp) => parseInt(comp.is_new) === 1
                  ).length
                }
                barColor="#EC87C0"
              />
            </Grid>
          </Grid>
        )}
        <Box my={4}>
          <Typography variant="h5">Signal Details</Typography>
        </Box>
        <Box px={2}>
          <Grid container spacing={2}>
            <Grid item xs={1}>
              <Typography variant="body1" color="textSecondary">
                ID
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body1" color="textSecondary">
                Company Name
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography variant="body1" color="textSecondary">
                Company ID
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography variant="body1" color="textSecondary">
                Responded
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body1" color="textSecondary">
                Suggested Categories
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body1" color="textSecondary">
                Responded Categoties
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body1" color="textSecondary">
                Actions
              </Typography>
            </Grid>
          </Grid>
        </Box>
        {generalData &&
          generalData.companies &&
          generalData.companies.map((company) => (
            <Box my={2} key={company.id.toString()}>
              <Paper>
                <SignalItem
                  company={company}
                  onHandle={handleCompanyHandled}
                  onViewPotentialCandidates={handleViewPotentialCandidates}
                  isLoading={buttonLoading === company.id}
                />
              </Paper>
            </Box>
          ))}
      </Container>
      {companyPotentialDetails &&
        parseInt(companyPotentialDetails.company.id) > 0 && (
          <SignalManagerCompanyPotentialCandidates
            companyDetails={companyPotentialDetails}
            onClose={() => setCompanyPotentialDetails(null)}
          />
        )}
    </React.Fragment>
  );
};

export default withRouter(SignalManager);

const SignalItem = ({
  company,
  onHandle,
  onViewPotentialCandidates,
  isLoading,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  return (
    <Grid container spacing={2} className={classes.companyItemRoot}>
      <Grid item xs={1}>
        <Box display={"flex"} justifyContent="flex-start" alignItems={"center"}>
          {parseInt(company.is_new) ? (
            <div
              className={classes.dot}
              style={{ backgroundColor: theme.palette.secondary.main }}
            ></div>
          ) : (
            <div
              className={classes.dot}
              style={{ backgroundColor: theme.palette.grey[500] }}
            ></div>
          )}
          <Typography variant="body1">{company.id}</Typography>
        </Box>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1">{company.name}</Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography variant="body1">{company.company_id}</Typography>
      </Grid>
      <Grid item xs={1}>
        {company.responded_at ? (
          <LastModified toDate={company.responded_at} />
        ) : (
          ""
        )}
      </Grid>
      <Grid item xs={3}>
        <ConfigurationListsContext.Consumer>
          {(context) =>
            company.suggested_categories &&
            company.suggested_categories.length > 0 &&
            context.professionsList.length > 0
              ? company.suggested_categories.map((sc) => (
                  <Chip
                    key={sc}
                    size="small"
                    label={
                      context.professionsList.find((p) => p.id === parseInt(sc))
                        .name
                    }
                  />
                ))
              : ""
          }
        </ConfigurationListsContext.Consumer>
      </Grid>
      <Grid item xs={2}>
        <ConfigurationListsContext.Consumer>
          {(context) =>
            company.responded_categories &&
            company.responded_categories.length > 0 &&
            context.professionsList.length > 0
              ? company.responded_categories.map((rc) =>
                  parseInt(rc) > 0 ? (
                    <Chip
                      key={rc}
                      size="small"
                      label={
                        context.professionsList.find(
                          (p) => p.id === parseInt(rc)
                        ).name
                      }
                    />
                  ) : (
                    ""
                  )
                )
              : ""
          }
        </ConfigurationListsContext.Consumer>
      </Grid>
      <Grid item xs={2}>
        <Box
          display={"flex"}
          justifyContent="space-between"
          alignItems={"center"}
          maxWidth={"150px"}
        >
          <Tooltip arrow title="Mark as handled">
            <IconButton
              className={classes.actionButtonHandle}
              onClick={() => onHandle(company.id)}
            >
              {parseInt(company.is_new) === 1 ? (
                <SendFill />
              ) : (
                <CheckRoundedIcon />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onViewPotentialCandidates(company.id)}
          >
            {isLoading ? "LOADIND..." : "SHOW"}
            {isLoading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};
